import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../components/auth/AuthContext';
import * as Sentry from "@sentry/react";

// Sentry logging for critical actions only
const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'ssh-key-selector',
    level: 'info',
    data: context
  });
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Validate SSH key object structure
const validateSSHKeyObject = (key) => {
  if (!key || typeof key !== 'object') return false;
  if (!key.id || (typeof key.id !== 'string' && typeof key.id !== 'number')) return false;
  if (!key.name || typeof key.name !== 'string') return false;
  return true;
};

// Validate SSH keys array
const validateSSHKeysArray = (keys) => {
  if (!Array.isArray(keys)) return false;
  return keys.every(key => validateSSHKeyObject(key));
};

// Process SSH keys from props with validation
const processSSHKeysFromProps = (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) {
    return [];
  }
  
  if (!validateSSHKeysArray(keys)) {
    return [];
  }
  
  return keys.map(key => ({
    ...key,
    id: String(key.id),
    name: sanitizeText(key.name)
  }));
};

const SSHKeySelector = ({ 
  selectedKey, 
  onKeyChange, 
  onImportKey, 
  onCreateKey, 
  onRefresh,
  sshKeys = [],
  disabled = false,
  disabledMessage = '',
  isModalOpen = false
}) => {
  const [localKeys, setLocalKeys] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedKeyName, setSelectedKeyName] = useState('');
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  
  // Loading states for UI interactions
  const [loadingStates, setLoadingStates] = useState({
    selecting: false
  });
  
  const { isAuthenticated } = useAuth();
  
  // Clear error state
  const clearError = () => {
    setError(null);
  };
  
  // Handle key selection
  const handleSelectKey = (keyId, keyName) => {
    logUserAction('ssh_key_selected', { keyId, keyName });
    
    try {
      setLoadingStates(prev => ({ ...prev, selecting: true }));
      clearError();
      
      // Validate selection parameters
      if (!keyId || typeof keyId !== 'string') {
        throw new Error('Invalid key ID');
      }
      
      if (!keyName || typeof keyName !== 'string') {
        throw new Error('Invalid key name');
      }
      
      if (typeof onKeyChange !== 'function') {
        throw new Error('Invalid key change handler');
      }
      
      // Validate that the key exists in local keys
      const keyExists = localKeys.some(key => 
        validateSSHKeyObject(key) && key.id === keyId
      );
      
      if (!keyExists) {
        throw new Error('Selected SSH key not found');
      }
      
      const safeName = sanitizeText(keyName);
      onKeyChange(keyId);
      setSelectedKeyName(safeName);
      setIsDropdownOpen(false);
      
    } catch (error) {
      setError(error.message || 'Failed to select SSH key');
    } finally {
      setLoadingStates(prev => ({ ...prev, selecting: false }));
    }
  };
  
  // Handle import key
  const handleImportKey = () => {
    logUserAction('ssh_key_import_clicked');
    
    try {
      clearError();
      
      if (typeof onImportKey !== 'function') {
        throw new Error('Invalid import handler');
      }
      
      onImportKey();
      
    } catch (error) {
      setError(error.message || 'Failed to open import dialog');
    }
  };
  
  // Handle create key
  const handleCreateKey = () => {
    logUserAction('ssh_key_create_clicked');
    
    try {
      clearError();
      
      if (typeof onCreateKey !== 'function') {
        throw new Error('Invalid create handler');
      }
      
      onCreateKey();
      
    } catch (error) {
      setError(error.message || 'Failed to open create dialog');
    }
  };
  
  // Handle refresh keys
  const handleRefreshKeys = () => {
    logUserAction('ssh_keys_refresh_clicked');
    
    if (!isAuthenticated) {
      setError('Please log in to refresh SSH keys');
      return;
    }
    
    if (typeof onRefresh === 'function') {
      clearError();
      onRefresh();
    } else {
      setError('Refresh function not available');
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  
  // Update local keys when prop changes with validation
useEffect(() => {
  if (Array.isArray(sshKeys)) {
    const processedKeys = processSSHKeysFromProps(sshKeys);
    setLocalKeys(processedKeys);
    
    if (selectedKey) {
      const selected = processedKeys.find(k => k.id === selectedKey);
      if (selected) {
        setSelectedKeyName(selected.name);
      } else {
        setSelectedKeyName('');
      }
    } else {
      setSelectedKeyName('');
    }
  }
}, [sshKeys, selectedKey]);
  
  // Toggle dropdown
  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };
  
  // Prevent closing when clicking inside dropdown
  const handleDropdownClick = (e) => {
    e.stopPropagation();
  };

  // Error display component
  const ErrorDisplay = ({ message }) => (
    <div className="text-red-400 text-sm md:text-xs mb-3 md:mb-2 bg-red-900 bg-opacity-20 p-2 md:p-1 rounded">
      <div className="flex items-start">
        <svg className="h-4 w-4 text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <p>{sanitizeText(message)}</p>
      </div>
    </div>
  );

  const hasKeys = Array.isArray(localKeys) && localKeys.length > 0;
  const isValidKeysData = validateSSHKeysArray(localKeys);
  
  return (
    <>
      <style>
        {`
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #555555 #2a2a2a;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #2a2a2a;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #555555;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #666666;
          }
        `}
      </style>
      <div className="bg-[#121212] p-3 md:p-1.5 rounded-md mb-4 md:mb-1.5 w-full md:w-[90%] md:mx-auto">
        <h3 className="font-semibold text-[#e0eacc] mb-3 md:mb-1 text-sm md:text-xs">SSH Key</h3>

        {/* Show data validation error */}
        {hasKeys && !isValidKeysData && (
          <ErrorDisplay message="SSH key data is invalid. Please refresh the page." />
        )}
        
        {/* Show operation error if exists */}
        {error && (
          <ErrorDisplay message={error} />
        )}
        
        {hasKeys && isValidKeysData ? (
          <div className="flex flex-col gap-3 md:gap-2">
            {/* Custom dropdown */}
            <div className="relative w-full">
              <div 
                className={`w-full p-3 md:p-1.5 bg-[#1d1d1d] border border-[#333333] ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#e0eacc] cursor-pointer'} rounded text-white text-sm flex justify-between items-center ${
                  loadingStates.selecting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={disabled ? undefined : toggleDropdown}
                role="button"
                tabIndex={0}
                aria-expanded={isDropdownOpen}
                aria-haspopup="listbox"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleDropdown(e);
                  }
                }}
              >
                <span>{disabled && disabledMessage ? sanitizeText(disabledMessage) : (selectedKeyName || 'Select an SSH key')}</span>
                <div className="flex items-center gap-1">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div 
                      key={i}
                      className="w-1 h-1 bg-[#cad9e3] rounded-full animate-bounce"
                      style={{ 
                        animationDelay: `${i * 150}ms`,
                        animationDuration: '1s',
                        animationIterationCount: 'infinite'
                      }}
                    />
                  ))}
                </div>
              </div>
              
              {/* Dropdown menu */}
              {isDropdownOpen && (
                <div 
                  className="absolute z-10 left-0 right-0 mt-1 bg-[#1d1d1d] border border-[#333333] rounded shadow-lg max-h-32 overflow-auto custom-scrollbar"
                  onClick={handleDropdownClick}
                  ref={dropdownRef}
                  role="listbox"
                >
                  <div className="py-1">
                    {localKeys.map((key) => {
                      if (!validateSSHKeyObject(key)) {
                        return null;
                      }
                      
                      return (
                        <div 
                          key={key.id} 
                          className={`px-3 md:px-2 py-3 md:py-1.5 cursor-pointer flex items-center ${
                            key.id === selectedKey 
                              ? 'bg-[#252525] text-[#e0eacc]' 
                              : 'text-gray-200 hover:bg-[#252525] hover:text-[#e0eacc]'
                          }`}
                          onClick={() => handleSelectKey(key.id, key.name)}
                          role="option"
                          aria-selected={key.id === selectedKey}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSelectKey(key.id, key.name);
                            }
                          }}
                          tabIndex={0}
                        >
                          {sanitizeText(key.name)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="flex justify-center gap-3 md:gap-4">
              <button
                onClick={handleImportKey}
                disabled={isModalOpen}
                className={`px-6 md:px-8 bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] hover:text-[#e0eacc] text-white py-2 md:py-1.5 rounded text-sm transition-all ${
                  isModalOpen ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                IMPORT
              </button>
              <button
                onClick={handleCreateKey}
                disabled={isModalOpen}
                className={`px-6 md:px-8 bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] hover:text-[#e0eacc] text-white py-2 md:py-1.5 rounded text-sm transition-all ${
                  isModalOpen ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                CREATE
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 md:gap-2">
            <div className="p-3 md:p-1.5 bg-[#1d1d1d] border border-[#333333] rounded text-gray-400 text-sm flex items-center">
              No SSH keys available
            </div>
            
            {/* Action buttons */}
            <div className="flex justify-center gap-3 md:gap-4">
              <button
                onClick={handleImportKey}
                disabled={isModalOpen}
                className={`px-4 md:px-6 bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] hover:text-[#e0eacc] text-white py-2 md:py-1.5 rounded text-sm transition-all ${
                  isModalOpen ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                IMPORT
              </button>
              <button
                onClick={handleCreateKey}
                disabled={isModalOpen}
                className={`px-4 md:px-6 bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] hover:text-[#e0eacc] text-white py-2 md:py-1.5 rounded text-sm transition-all ${
                  isModalOpen ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                CREATE
              </button>
            </div>
          </div>
        )}
        
        {/* Refresh button */}
        <div className="flex justify-center mt-3 md:mt-2">
          <button 
            onClick={handleRefreshKeys}
            disabled={!isAuthenticated}
            className={`text-sm md:text-xs text-gray-400 hover:text-[#e0eacc] transition-colors flex items-center gap-1 py-2 md:py-1 ${
              !isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="fill-current md:w-[10px] md:h-[10px]">
              <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"/>
            </svg>
            Refresh Keys
          </button>
        </div>
      </div>
    </>
  );
};

// PropTypes for type checking
SSHKeySelector.propTypes = {
  selectedKey: PropTypes.string,
  onKeyChange: PropTypes.func.isRequired,
  onImportKey: PropTypes.func.isRequired, 
  onCreateKey: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
  sshKeys: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  disabled: PropTypes.bool,
  disabledMessage: PropTypes.string,
  isModalOpen: PropTypes.bool
};

// Default props
SSHKeySelector.defaultProps = {
  selectedKey: '',
  sshKeys: [],
  onRefresh: null,
  disabled: false,
  disabledMessage: '',
  isModalOpen: false
};

export default React.memo(SSHKeySelector);